/**
 * AI Companion Widget
 * A gentle, persistent AI presence throughout the app
 * Shares conversation state with AIGuideHero via SharedAIContext
 * 
 * IMPORTANT: On pages where AIGuideHero is visible (like /home dashboard),
 * the floating button is hidden to avoid duplication. The conversation
 * state is fully shared, so opening the widget elsewhere continues the same chat.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, X, Send, ChevronUp, ChevronDown, MessageCircle, ChevronRight, ExternalLink } from 'lucide-react';
import { SmallText, BodyText } from './brand/BrandTypography';
import { useAIGuide } from '../context/AIGuideContext';
import { useSharedAI } from '../context/SharedAIContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map action tags to routes and display names
const ACTION_TAG_MAP = {
    // Check-in / Assessment
    'GO_TO_CHECK_IN': { route: '/initial-assessment', name: 'Quick Check-In' },
    'START_INITIAL_ASSESSMENT': { route: '/initial-assessment', name: 'Assessment' },
    'GO_TO_ASSESSMENT': { route: '/initial-assessment', name: 'Assessment' },
    // Regulation Tools
    'GO_TO_STILL_SPHERE': { route: '/regulate/still-sphere', name: 'Still Sphere' },
    'GO_TO_CALM_CYCLE': { route: '/regulate/breathing-orb', name: 'Calm Cycle' },
    'GO_TO_MICRO_RESET': { route: '/regulate/micro-reset', name: 'Micro Reset' },
    'GO_TO_MICRO_RESET_BREATH': { route: '/regulate/micro-reset?focus=breath', name: 'Micro Reset (Breathing)' },
    'GO_TO_MICRO_RESET_SOMATIC': { route: '/regulate/micro-reset?focus=somatic', name: 'Micro Reset (Body)' },
    'GO_TO_MICRO_RESET_GROUNDING': { route: '/regulate/micro-reset?focus=grounding', name: 'Micro Reset (Grounding)' },
    'GO_TO_MICRO_RESET_MOVEMENT': { route: '/regulate/micro-reset?focus=movement', name: 'Micro Reset (Movement)' },
    'GO_TO_MICRO_RESET_MINDFUL': { route: '/regulate/micro-reset?focus=mindful', name: 'Micro Reset (Mindful)' },
    'GO_TO_NAVIGATOR': { route: '/regulate/regulation-navigator', name: 'Regulation Navigator' },
    'GO_TO_BOX_BREATHING': { route: '/regulate/box-breathing', name: 'Box Breathing' },
    'GO_TO_BODY_SCAN': { route: '/regulate/body-scan', name: 'Body Scan' },
    'GO_TO_PMR': { route: '/regulate/pmr', name: 'Progressive Muscle Relaxation' },
    'GO_TO_SOMATIC': { route: '/regulate/somatic', name: 'Somatic Practices' },
    'GO_TO_GUIDED_MEDITATIONS': { route: '/regulate/guided-meditations', name: 'Guided Meditations' },
    'GO_TO_RSD_MEDITATION': { route: '/regulate/guided-meditations?meditation=rsd-healing', name: 'RSD Meditation' },
    'GO_TO_CALMING_ANXIETY_MEDITATION': { route: '/regulate/guided-meditations?meditation=calming-anxiety', name: 'Calming Anxiety Meditation' },
    'GO_TO_SELF_TALK': { route: '/regulate/self-talk', name: 'Self-Talk Reframes' },
    'GO_TO_EMDR': { route: '/regulate/emdr', name: 'Bilateral Regulation' },
    'GO_TO_EMDR_VISUAL': { route: '/regulate/emdr?mode=visual', name: 'Bilateral (Visual)' },
    'GO_TO_EMDR_AUDIO': { route: '/regulate/emdr?mode=audio', name: 'Bilateral (Audio)' },
    'GO_TO_REGULATION_TOOLS': { route: '/regulate', name: 'Regulation Tools' },
    'GO_TO_HYPNOTHERAPY': { route: '/hypnotherapy', name: 'Hypnotherapy' },
    
    // Breathing Patterns
    'GO_TO_BREATHING_PATTERNS': { route: '/regulate/breathing-patterns', name: 'Breathing Patterns' },
    'GO_TO_BREATHING_PHYSIOLOGICAL_SIGH': { route: '/regulate/breathing-patterns?pattern=physiological-sigh', name: 'Physiological Sigh' },
    'GO_TO_BREATHING_478': { route: '/regulate/breathing-patterns?pattern=4-7-8', name: '4-7-8 Breathing' },
    'GO_TO_BREATHING_BOX': { route: '/regulate/box-breathing', name: 'Box Breathing' },
    'GO_TO_BREATHING_EXTENDED_EXHALE': { route: '/regulate/breathing-patterns?pattern=extended-exhale', name: 'Extended Exhale' },
    'GO_TO_BREATHING_COHERENT': { route: '/regulate/breathing-patterns?pattern=coherent', name: 'Coherent Breathing' },
    
    // Relationship Hub
    'GO_TO_RELATIONSHIP_HUB': { route: '/relationship-hub', name: 'Relationship Hub' },
    'GO_TO_RELATIONSHIP_CHECK_IN': { route: '/relationship-hub/check-in', name: 'Relationship Check-In' },
    'GO_TO_BREAK_THE_CYCLE': { route: '/relationship-hub/break-the-cycle', name: 'Break the Cycle' },
    'GO_TO_WHICH_CYCLE': { route: '/relationship-hub/cycles', name: 'Which Cycle Are We In?' },
    'GO_TO_ATTACHMENT_NEEDS': { route: '/relationship-hub/attachment-needs', name: 'Attachment Needs' },
    'GO_TO_SOLO_NEED': { route: '/relationship-hub/solo-need', name: 'What Do I Need?' },
    'GO_TO_REPAIR': { route: '/relationship-hub/repair', name: 'Repair & Reconnection' },
    'GO_TO_RELATIONSHIP_SAFETY': { route: '/regulate/relationship-check', name: 'Relationship Safety Check' },
    'GO_TO_COREGULATION': { route: '/regulate/coregulation', name: 'Co-Regulation' },
    'GO_TO_PARTNER_MODE': { route: '/regulate/partner-mode', name: 'Partner Mode' },
    'GO_TO_PARTNER_SUPPORT': { route: '/regulate/adhd-partner-support', name: 'Partner Support Guide' },
    'GO_TO_ADHD_TRIGGERS': { route: '/adhd-relationships', name: 'ADHD Relational Triggers' },
    'GO_TO_INFINITY_LOOP': { route: '/relationship-hub/infinity-loop', name: 'Infinity Loop' },
    
    // Settling tools
    'GO_TO_SETTLE_STILL_SPHERE': { route: '/regulate/still-sphere', name: 'Still Sphere' },
    'GO_TO_SETTLE_CALM_CYCLE': { route: '/regulate/breathing-orb', name: 'Calm Cycle' },
    'GO_TO_SETTLE_MICRO_RESET': { route: '/regulate/micro-reset', name: 'Micro Reset' },
    'GO_TO_SETTLE_BOX_BREATHING': { route: '/regulate/box-breathing', name: 'Box Breathing' },
    'GO_TO_SETTLE_NAVIGATOR': { route: '/regulate/regulation-navigator', name: 'Navigator' },
    
    // Crisis
    'GO_TO_CRISIS': { route: '/crisis', name: 'Crisis Resources' },
};

// Parse message content to extract action tags
const parseMessageWithActions = (content) => {
    if (!content) return { text: '', actions: [] };
    
    const actionTagRegex = /\[([A-Z_]+)\]/g;
    const actions = [];
    let match;
    
    while ((match = actionTagRegex.exec(content)) !== null) {
        const tag = match[1];
        if (ACTION_TAG_MAP[tag]) {
            actions.push({
                tag,
                ...ACTION_TAG_MAP[tag]
            });
        }
    }
    
    // Remove action tags from the text
    const cleanText = content.replace(/\s*\[[A-Z_]+\]\s*/g, ' ').trim();
    
    return { text: cleanText, actions };
};

// Contextual prompts based on where the user is
const CONTEXTUAL_PROMPTS = {
    settling: ["I'm feeling stuck", "This is hard", "I need help"],
    assessment: ["I'm not sure", "Can we slow down?", "What does this mean?"],
    tool_intro: ["I'm nervous", "Will this help?", "What should I expect?"],
    tool_active: ["This is hard", "I need to pause", "I'm feeling worse"],
    tool_complete: ["I feel better", "I'm still struggling", "What's next?"],
    dashboard: ["I don't know what I need", "Everything feels like too much", "Help me choose"],
    education: ["This is a lot", "I need a break", "Can you explain?"],
    default: ["I'm overwhelmed", "I need something quick", "I don't know what I need"]
};

const AICompanion = ({ variant = 'floating', initialOpen = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        userState, 
        updateContext, 
        getContextualGuidance,
        isLoading: guideLoading,
        lastGuidance,
        APP_CONTEXTS 
    } = useAIGuide();
    
    // Use shared AI context for conversation continuity
    const { 
        messages, 
        setMessages, 
        pendingNavigation,
        setNavigation,
        hasInteracted,
        sessionId,
        isLoadingHistory
    } = useSharedAI();
    
    const [isOpen, setIsOpen] = useState(initialOpen);
    const [isMinimized, setIsMinimized] = useState(false);
    const [input, setInput] = useState('');
    const [showPrompts, setShowPrompts] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    
    // Check if we're on the dashboard/home page where AIGuideHero is visible
    const isOnDashboard = location.pathname === '/home' || location.pathname === '/dashboard';

    // Track first message state
    const [isFirstMessage, setIsFirstMessage] = useState(true);

    // Get current context from URL
    const getCurrentContext = () => {
        const path = location.pathname;
        if (path.includes('settling')) return 'settling';
        if (path.includes('assessment') || path.includes('check-in')) return 'assessment';
        if (path.includes('regulate/') && !path.includes('navigator')) return 'tool_active';
        if (path.includes('home')) return 'dashboard';
        if (path.includes('education') || path.includes('adhd')) return 'education';
        if (path.includes('complete')) return 'tool_complete';
        return 'default';
    };

    const currentContext = getCurrentContext();
    const prompts = CONTEXTUAL_PROMPTS[currentContext] || CONTEXTUAL_PROMPTS.default;

    // Scroll to bottom within container only (not the whole page)
    useEffect(() => {
        if (messagesContainerRef.current) {
            setTimeout(() => {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }, 50);
        }
    }, [messages, isLoading]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && !isMinimized && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, isMinimized]);

    const sendMessage = async (messageText) => {
        if (!messageText.trim() || isLoading) return;

        const userMessage = { role: 'user', content: messageText.trim() };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setShowPrompts(false);
        setIsLoading(true);
        
        // Clear any pending navigation when starting a new message
        setNavigation(null);

        // Check if user prefers to skip settling (3+ skips in a row)
        const skipCount = parseInt(localStorage.getItem('coregulate_settle_skip_count') || '0');
        const settlingPreference = skipCount >= 3 ? 'skip_settling' : 'offer_settling';

        // Action routes mapping
        const actionRoutes = {
            'start_initial_assessment': '/initial-assessment',
            'go_to_check_in': '/initial-assessment',
            'go_to_assessment': '/initial-assessment',
            'start_relationship_assessment': '/relationship-hub/check-in',
            'go_to_still_sphere': '/regulate/still-sphere',
            'go_to_calm_cycle': '/regulate/breathing-orb',
            'go_to_micro_reset': '/regulate/micro-reset',
            'go_to_micro_reset_breath': '/regulate/micro-reset?focus=breath',
            'go_to_micro_reset_somatic': '/regulate/micro-reset?focus=somatic',
            'go_to_micro_reset_grounding': '/regulate/micro-reset?focus=grounding',
            'go_to_micro_reset_movement': '/regulate/micro-reset?focus=movement',
            'go_to_micro_reset_mindful': '/regulate/micro-reset?focus=mindful',
            'go_to_box_breathing': '/regulate/box-breathing',
            'go_to_navigator': '/regulate/navigator',
            'go_to_regulation_tools': '/regulate',
            'go_to_dashboard': '/home',
            'go_to_partner_support': '/regulate/adhd-partner-support',
            'go_to_body_scan': '/regulate/body-scan',
            'go_to_pmr': '/regulate/pmr',
            'go_to_somatic': '/regulate/somatic',
            'go_to_guided_meditations': '/regulate/guided-meditations',
            'go_to_rsd_meditation': '/regulate/guided-meditations?meditation=rsd-healing',
            'go_to_breathing_patterns': '/regulate/breathing-patterns',
            'go_to_crisis': '/crisis',
            'go_to_emdr': '/regulate/emdr',
            'go_to_emdr_visual': '/regulate/emdr?mode=visual',
            'go_to_emdr_audio': '/regulate/emdr?mode=audio',
            // Specific breathing pattern routes
            'go_to_breathing_physiological_sigh': '/regulate/breathing-patterns?pattern=physiological-sigh',
            'go_to_breathing_478': '/regulate/breathing-patterns?pattern=4-7-8',
            'go_to_breathing_box': '/regulate/breathing-patterns?pattern=box',
            'go_to_breathing_extended_exhale': '/regulate/breathing-patterns?pattern=extended-exhale',
            'go_to_breathing_coherent': '/regulate/breathing-patterns?pattern=coherent',
            // Educational content - Learn More routes (navigate to Understanding ADHD page)
            'learn_more_rsd': '/understanding-adhd',
            'learn_more_emotional_dysregulation': '/understanding-adhd',
            'learn_more_time_blindness': '/understanding-adhd',
            'learn_more_masking': '/understanding-adhd',
            'learn_more_hyperfocus': '/understanding-adhd',
            'learn_more_coregulation': '/understanding-adhd',
            'learn_more_adhd_mental_health': '/understanding-adhd',
            'learn_more_adhd_relationships': '/understanding-adhd',
            'learn_more_attachment_styles': '/understanding-adhd',
            'learn_more_negative_cycles': '/understanding-adhd',
        };
        
        // Get friendly name for action
        const getActionName = (action) => {
            const names = {
                'go_to_still_sphere': 'Still Sphere',
                'go_to_calm_cycle': 'Calm Cycle',
                'go_to_micro_reset': 'Micro Reset',
                'go_to_micro_reset_breath': 'Micro Reset (Breathing)',
                'go_to_micro_reset_somatic': 'Micro Reset (Body)',
                'go_to_micro_reset_grounding': 'Micro Reset (Grounding)',
                'go_to_micro_reset_movement': 'Micro Reset (Movement)',
                'go_to_micro_reset_mindful': 'Micro Reset (Mindful)',
                'go_to_box_breathing': 'Box Breathing',
                'go_to_navigator': 'Navigator',
                'go_to_regulation_tools': 'Regulation Tools',
                'start_initial_assessment': 'Check-In',
                'start_relationship_assessment': 'Relationship Check-In',
                'go_to_partner_support': 'Partner Support',
                'go_to_body_scan': 'Body Scan',
                'go_to_pmr': 'Muscle Relaxation',
                'go_to_emdr': 'EMDR',
                'go_to_emdr_visual': 'EMDR (Visual)',
                'go_to_emdr_audio': 'EMDR (Audio)',
                'go_to_somatic': 'Somatic Practices',
                'go_to_guided_meditations': 'Guided Meditations',
                'go_to_breathing_patterns': 'Breathing Patterns',
                'go_to_crisis': 'Crisis Resources',
                'go_to_dashboard': 'Dashboard',
                // Specific breathing patterns
                'go_to_breathing_physiological_sigh': 'Physiological Sigh (30 sec)',
                'go_to_breathing_478': '4-7-8 Breathing',
                'go_to_breathing_box': 'Box Breathing',
                'go_to_breathing_extended_exhale': 'Extended Exhale',
                'go_to_breathing_coherent': 'Coherent Breathing',
                // Learn More actions
                'learn_more_rsd': 'Learn about RSD',
                'learn_more_emotional_dysregulation': 'Learn about Emotional Dysregulation',
                'learn_more_time_blindness': 'Learn about Time Blindness',
                'learn_more_masking': 'Learn about Masking',
                'learn_more_hyperfocus': 'Learn about Hyperfocus',
                'learn_more_coregulation': 'Learn about Co-Regulation',
                'learn_more_adhd_mental_health': 'Learn about ADHD & Mental Health',
                'learn_more_adhd_relationships': 'Learn about ADHD in Relationships',
                'learn_more_attachment_styles': 'Learn about Attachment Styles',
                'learn_more_negative_cycles': 'Learn about Negative Cycles',
            };
            return names[action] || 'Tool';
        };

        try {
            const token = localStorage.getItem('co_regulate_token');
            
            // Get user's local hour for time-aware greetings
            const userLocalHour = new Date().getHours();
            
            const response = await fetch(`${API_URL}/api/guide/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    message: messageText,
                    session_id: sessionId,
                    has_completed_assessment: !!userState.assessmentData,
                    context: currentContext,
                    settling_preference: settlingPreference,
                    user_local_hour: userLocalHour,
                    is_first_message: isFirstMessage
                })
            });

            // After first message, set flag to false
            if (isFirstMessage) {
                setIsFirstMessage(false);
            }

            if (response.ok) {
                const data = await response.json();
                
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: data.response,
                    action: data.action
                }]);

                // Store pending navigation instead of auto-navigating
                // User will click a button to navigate
                if (data.action && actionRoutes[data.action]) {
                    setNavigation({
                        action: data.action,
                        route: actionRoutes[data.action],
                        name: getActionName(data.action)
                    });
                }
            } else {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: "I'm here with you. Would you like to try a grounding tool, or would it help to just pause for a moment?"
                }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Let's take this slowly. What feels most needed right now?"
            }]);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle user-initiated navigation
    const handleNavigate = () => {
        if (pendingNavigation) {
            navigate(pendingNavigation.route);
            setIsOpen(false); // Close the companion panel after navigation
        }
    };

    // Don't show on certain pages
    // Also hide floating variant on dashboard where AIGuideHero is visible
    // Note: Auth page uses /auth?mode=login format, so we check for /auth as well
    const hiddenPaths = ['/login', '/register', '/admin', '/landing', '/auth'];
    const shouldHideCompletely = hiddenPaths.some(p => location.pathname.startsWith(p));
    
    if (shouldHideCompletely) {
        return null;
    }
    
    // For floating variant on dashboard, hide completely since AIGuideHero handles AI there
    if (variant === 'floating' && isOnDashboard) {
        return null;
    }

    if (variant === 'inline') {
        return (
            <div className="bg-[#FBE0C3]/50 rounded-2xl p-4 border border-[#FFBB98]">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#7D8E95]/30 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-[#1D6B6B]" />
                    </div>
                    <div className="flex-1">
                        {messages.length === 0 ? (
                            <>
                                <BodyText className="text-[#2A8585] mb-3">
                                    I'm here if you need anything. How are you feeling?
                                </BodyText>
                                <div className="flex flex-wrap gap-2">
                                    {prompts.map((prompt, idx) => {
                                        const iconColors = [
                                            'bg-gradient-to-br from-[#FFBB98] to-[#E09060]', // Coral
                                            'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]', // Teal
                                            'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]'  // Dusty Rose
                                        ];
                                        const emojis = ['😔', '🌀', '💭'];
                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => sendMessage(prompt)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 hover:bg-white/90 backdrop-blur-sm border border-white/60 hover:border-[#2A8585]/30 text-[#1D6B6B] text-xs font-medium transition-all duration-300 hover:shadow-md active:scale-95 whitespace-nowrap"
                                            >
                                                <span className={`w-5 h-5 rounded-md ${iconColors[idx % iconColors.length]} flex items-center justify-center text-white text-[10px]`}>
                                                    {emojis[idx % emojis.length]}
                                                </span>
                                                {prompt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="space-y-3">
                                {messages.slice(-4).map((msg, idx) => {
                                    // Parse message for action tags
                                    const { text: cleanText, actions } = msg.role === 'assistant' 
                                        ? parseMessageWithActions(msg.content)
                                        : { text: msg.content, actions: [] };
                                    
                                    return (
                                    <div key={idx} className={msg.role === 'user' ? 'text-right' : ''}>
                                        <span className={`inline-block px-3 py-2 rounded-2xl text-sm ${
                                            msg.role === 'user' 
                                                ? 'bg-[#7D8E95]/20 text-[#1D6B6B]' 
                                                : 'bg-white text-[#1D6B6B]'
                                        }`}>
                                            {cleanText}
                                        </span>
                                        {/* Subtle tool links for inline variant */}
                                        {actions.length > 0 && msg.role === 'assistant' && (
                                            <div className="mt-1.5">
                                                <span className="text-[10px] text-[#7D8E95]/60">Go to: </span>
                                                {actions.map((action, actionIndex) => (
                                                    <button
                                                        key={actionIndex}
                                                        onClick={() => navigate(action.route)}
                                                        className="text-xs text-[#1D6B6B] hover:text-[#155555] underline underline-offset-2 mr-2"
                                                        data-testid={`inline-tool-link-${action.tag.toLowerCase()}`}
                                                    >
                                                        {action.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )})}
                                {/* Navigation link removed - now using inline tool links */}
                                {isLoading && (
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" />
                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '150ms' }} />
                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '300ms' }} />
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {messages.length > 0 && (
                            <div className="mt-3 flex gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
                                    placeholder="Type here..."
                                    className="flex-1 px-3 py-2 text-sm rounded-full bg-white border border-[#FFBB98] focus:outline-none focus:border-[#7D8E95]"
                                />
                                <button
                                    onClick={() => sendMessage(input)}
                                    disabled={!input.trim() || isLoading}
                                    className="w-8 h-8 rounded-full bg-[#1D6B6B] text-white flex items-center justify-center disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Floating variant
    return (
        <>
            {/* Floating button - with gradient */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-20 right-6 z-[55] w-14 h-14 rounded-full text-white shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                    style={{
                        background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 50%, #145555 100%)',
                        boxShadow: '0 4px 20px rgba(52,70,72,0.35)'
                    }}
                    data-testid="ai-companion-btn"
                >
                    <MessageCircle className="w-6 h-6" />
                </button>
            )}

            {/* Chat panel */}
            {isOpen && (
                <div 
                    className={`fixed right-6 z-[55] bg-white rounded-2xl shadow-2xl border border-[#FFBB98]/40 transition-all duration-300 ${
                        isMinimized 
                            ? 'bottom-20 w-[280px] h-auto' 
                            : 'bottom-20 w-[340px]'
                    }`}
                    style={{ maxHeight: isMinimized ? 'auto' : 'calc(100vh - 140px)' }}
                >
                    {/* Header - with gradient */}
                    <div 
                        className="px-4 py-3 rounded-t-2xl border-b border-[#FFBB98]/30 flex items-center justify-between cursor-pointer"
                        style={{
                            background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 50%, #145555 100%)'
                        }}
                        onClick={() => setIsMinimized(!isMinimized)}
                    >
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                                <Sparkles className="w-3.5 h-3.5 text-white" />
                            </div>
                            <SmallText className="font-medium text-white">Sage</SmallText>
                        </div>
                        <div className="flex items-center gap-1">
                            {isMinimized ? (
                                <ChevronUp className="w-4 h-4 text-white/70" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-white/70" />
                            )}
                            <button 
                                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X className="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                    </div>

                    {!isMinimized && (
                        <>
                            {/* Messages */}
                            <div 
                                ref={messagesContainerRef}
                                className="h-[280px] overflow-y-auto p-4 space-y-3"
                            >
                                {messages.length === 0 ? (
                                    <div className="text-center py-2">
                                        <SmallText className="text-[#7D8E95] mb-3">
                                            I'm here if you need me.
                                        </SmallText>
                                    </div>
                                ) : (
                                    messages.map((msg, idx) => {
                                        // Parse message for action tags
                                        const { text: cleanText, actions } = msg.role === 'assistant' 
                                            ? parseMessageWithActions(msg.content)
                                            : { text: msg.content, actions: [] };
                                        
                                        return (
                                        <div
                                            key={idx}
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            {msg.role === 'user' ? (
                                                /* User message - simple rounded bubble */
                                                <div className="max-w-[85%] rounded-2xl px-3 py-2 bg-[#7D8E95]/20 text-[#1D6B6B]">
                                                    <SmallText className="leading-relaxed whitespace-pre-wrap">
                                                        {msg.content}
                                                    </SmallText>
                                                </div>
                                            ) : (
                                                /* Assistant message - speech bubble with tail on bottom-right */
                                                <div className="max-w-[85%] relative">
                                                    <div className="rounded-2xl px-3 py-2 bg-[#FBE0C3] text-[#1D6B6B]">
                                                        <SmallText className="leading-relaxed whitespace-pre-wrap">
                                                            {cleanText}
                                                        </SmallText>
                                                        {/* Subtle tool links */}
                                                        {actions.length > 0 && (
                                                            <div className="mt-2 pt-1.5 border-t border-[#7D8E95]/10">
                                                                <span className="text-[10px] text-[#7D8E95]/60">Go to: </span>
                                                                {actions.map((action, actionIndex) => (
                                                                    <button
                                                                        key={actionIndex}
                                                                        onClick={() => navigate(action.route)}
                                                                        className="text-xs text-[#1D6B6B] hover:text-[#155555] underline underline-offset-2 mr-2"
                                                                        data-testid={`companion-tool-link-${action.tag.toLowerCase()}`}
                                                                    >
                                                                        {action.name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Speech bubble tail - bottom right */}
                                                    <div 
                                                        className="absolute -bottom-1 right-3 w-2.5 h-2.5 bg-[#FBE0C3] transform rotate-45"
                                                        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )})
                                )}
                                
                                {/* Navigation link removed - now using inline tool links */}
                                
                                {isLoading && (
                                    <div className="flex justify-start">
                                        <div className="relative">
                                            <div className="bg-[#FBE0C3] rounded-2xl px-3 py-2">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" />
                                                    <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '150ms' }} />
                                                    <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '300ms' }} />
                                                </div>
                                            </div>
                                            {/* Speech bubble tail */}
                                            <div 
                                                className="absolute -bottom-1 right-3 w-2.5 h-2.5 bg-[#FBE0C3] transform rotate-45"
                                                style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Input */}
                            <div className="p-3 border-t border-[#FFBB98]">
                                <div className="flex gap-2">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
                                        placeholder={messages.length > 0 ? "Type a message..." : "How can I help?"}
                                        className="flex-1 px-3 py-2 text-sm rounded-full bg-[#FBE0C3] border border-[#FFBB98] focus:outline-none focus:border-[#7D8E95] placeholder-[#7D8E95]"
                                    />
                                    <button
                                        onClick={() => sendMessage(input)}
                                        disabled={!input.trim() || isLoading}
                                        className="w-8 h-8 rounded-full bg-[#1D6B6B] text-white flex items-center justify-center hover:bg-[#1D6B6B] disabled:opacity-50 transition-colors"
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                                {/* Quick prompts - Pill/Chip Design */}
                                <div className="mt-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                                    <div className="flex gap-1.5 w-max">
                                        {prompts.map((prompt, idx) => {
                                            const iconColors = [
                                                'bg-gradient-to-br from-[#FFBB98] to-[#E09060]', // Coral
                                                'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]', // Teal
                                                'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]'  // Dusty Rose
                                            ];
                                            const emojis = ['😔', '🌀', '💭'];
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => sendMessage(prompt)}
                                                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 hover:bg-white/90 backdrop-blur-sm border border-white/60 hover:border-[#2A8585]/30 text-[#1D6B6B] text-[10px] font-medium transition-all duration-300 hover:shadow-md active:scale-95 whitespace-nowrap flex-shrink-0"
                                                >
                                                    <span className={`w-4 h-4 rounded-md ${iconColors[idx % iconColors.length]} flex items-center justify-center text-white text-[8px]`}>
                                                        {emojis[idx % emojis.length]}
                                                    </span>
                                                    {prompt}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default AICompanion;
